import { useEffect, useMemo, useRef, useState } from "react";
import {
	Autocomplete,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Radio,
	RadioGroup,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import {
	AddCircleOutline,
	AutoMode,
	Close as CloseIcon,
	DeleteSweep,
	Payments as PaymentsIcon,
	RemoveCircleOutline,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { createFilterOptions, Util } from "../../util/Util";
import ConfirmDialog from "../../components/ConfirmDialog";
import Invoice from "../invoice/InvoiceReport";
import RefreshButton from "../../components/RefreshButton";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { useAppSocket } from "../../providers/AppSocketContext";
import { toast } from "react-toastify";
import { isWalletPayMode } from "../../util/constants";
import { MONEY_LIMITS, roundMoney, addMoney, compareMoney } from "../../util/quantity";
import {
	filterPayModesForWallet,
	isUsableWalletTender,
	useWalletTender,
	walletTenderCap,
} from "../wallet/useWalletTender";
import { Z_INDEX } from "../../styles/zIndex";
import { businessTodayDate } from "../../util/businessClock";
import { offerReceipt } from "../../util/receiptPrompt";

export type BulkPaymentOrder = "oldest" | "newest";

export interface UnpaidBill {
	batch: string;
	reference: string;
	date: string;
	amount: number;
	balance: number;
	paid?: number;
	user?: string;
	client?: string;
	agent?: string;
	inv_type?: string;
}

const isPayableBill = (bill: UnpaidBill) =>
	bill.balance > 0 && !Util.compareStrings(bill.inv_type || "", "Return");

export interface SelectedBill extends UnpaidBill {
	payAmount: number;
}

interface PaymentMode {
	id: string;
	mode_name: string;
}

interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle?: { client?: string; agent?: string; name?: string; acc_name?: string; is_agent?: boolean };
	onSuccess?: () => void;
}

const normalizeBill = (row: any): UnpaidBill => ({
	batch: row.batch,
	reference: row.reference || row.batch,
	date: row.date || row.Date,
	amount: Math.abs(Number(row.amount) || 0),
	balance: Math.abs(Number(row.balance) || 0),
	paid: Math.abs(Number(row.paid) || 0),
	user: row.user,
	client: row.client,
	agent: row.agent,
	inv_type: row.inv_type,
});

const billSortKey = (bill: UnpaidBill) => new Date(bill.date).getTime();

const allocateBills = (
	bills: UnpaidBill[],
	budget: number,
	order: BulkPaymentOrder,
): SelectedBill[] => {
	if (budget <= 0 || bills.length === 0) return [];

	const sorted = [...bills].sort((a, b) =>
		order === "oldest" ? billSortKey(a) - billSortKey(b) : billSortKey(b) - billSortKey(a),
	);

	const totalOwing = sorted.reduce((sum, bill) => addMoney(sum, bill.balance), 0);

	if (budget >= totalOwing) {
		return sorted.map((bill) => ({ ...bill, payAmount: bill.balance }));
	}

	let remaining = budget;
	const selected: SelectedBill[] = [];

	for (const bill of sorted) {
		if (remaining <= 0) break;
		const payAmount = Math.min(bill.balance, remaining);
		if (payAmount <= 0) continue;
		selected.push({ ...bill, payAmount });
		remaining = roundMoney(remaining - payAmount);
	}

	return selected;
};

const BulkPaymentModal = ({ isOpen, toggle, bundle, onSuccess }: Props) => {
	const theme = useTheme();
	const { appSettings } = useAppSocket();
	const { api, access } = useAuth();
	const { setLoadingMessage, queueReRun, t } = useUI();

	const themeColor = appSettings?.UI_THEME_PRIMARY?.value || theme.palette.primary.main;
	const currency = appSettings?.BIZ_CURRENCY?.value || "USD";
	const formatMoney = (value: number) => Util.formatCurrency(value, currency);

	const lockedAgentName = bundle?.agent || (bundle?.is_agent ? bundle?.name : "") || "";
	const lockedClientName = bundle?.client || "";
	const lockAgent = Boolean(lockedAgentName);
	const lockClient = Boolean(lockedClientName);
	const showAgentPicker = lockAgent || !lockClient;

	const [agents, setAgents] = useState<any[]>([]);
	const [clients, setClients] = useState<any[]>([]);
	const [selectedAgent, setSelectedAgent] = useState<any>(null);
	const [selectedClient, setSelectedClient] = useState<any>(null);
	const [availableBills, setAvailableBills] = useState<UnpaidBill[]>([]);
	const [selectedBills, setSelectedBills] = useState<SelectedBill[]>([]);
	const [autoAddOrder, setAutoAddOrder] = useState<BulkPaymentOrder>("oldest");

	const [payModes, setPayModes] = useState<PaymentMode[]>([]);
	const [selectedModeId, setSelectedModeId] = useState<string>("");
	const { wallet: clientWallet } = useWalletTender({
		clientId: selectedClient?.id,
		clientName: selectedClient?.acc_name,
		enabled: Boolean(selectedClient?.id || selectedClient?.acc_name),
	});
	const showWallet = Boolean(selectedClient?.acc_name) && isUsableWalletTender(clientWallet);
	const visibleModes = useMemo(
		() => filterPayModesForWallet(payModes, showWallet),
		[payModes, showWallet],
	);
	const walletCap = walletTenderCap(clientWallet);
	const [totalToPay, setTotalToPay] = useState<number>(0);
	const [selectedDate, setSelectedDate] = useState<Date>(() => businessTodayDate());
	const [payDetails, setPayDetails] = useState("");

	const [isAgentLoading, setIsAgentLoading] = useState(false);
	const [isClientLoading, setIsClientLoading] = useState(false);
	const [isBillsLoading, setIsBillsLoading] = useState(false);
	const [isModesLoading, setIsModesLoading] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [toViewInvoice, setToViewInvoice] = useState<{ batch: string } | null>(null);
	const [amountError, setAmountError] = useState("");

	const errorRef = useRef("");

	const allocated = useMemo(
		() => selectedBills.reduce((sum, bill) => addMoney(sum, (Number(bill.payAmount) || 0)), 0),
		[selectedBills],
	);
	const unallocated = Math.max(0, roundMoney(totalToPay - allocated));
	const allocationProgress = totalToPay > 0 ? Math.min(100, (allocated / totalToPay) * 100) : 0;
	const overAllocated = compareMoney(allocated, totalToPay) > 0 && totalToPay > 0;

	const hasBillScope = Boolean(selectedAgent?.acc_name || selectedClient?.acc_name);
	const agentScoped = Boolean(selectedAgent?.acc_name);
	const showClientColumn = agentScoped && !selectedClient?.acc_name;

	const walletModeSelected = isWalletPayMode(selectedModeId);
	const canValidate =
		hasBillScope &&
		selectedModeId &&
		totalToPay > 0 &&
		allocated > 0 &&
		!overAllocated &&
		!(walletModeSelected && totalToPay > walletCap) &&
		selectedBills.every(
			(b) => b.payAmount > 0 && compareMoney(b.payAmount, b.balance) <= 0 && b.payAmount <= MONEY_LIMITS.max,
		);

	const fetchAgents = async () => {
		setIsAgentLoading(true);
		const res = await api.get("/clients", { params: { Type: "CONSUMER", Active: true } });
		if (res?.status === 200) {
			const agentList = res.data.filter((c: any) => c.is_agent);
			agentList.sort((a: any, b: any) => a.acc_name.localeCompare(b.acc_name));
			setAgents(agentList);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchAgents);
		}
		setIsAgentLoading(false);
	};

	const fetchAllClients = async () => {
		setIsClientLoading(true);
		const res = await api.get("/clients", { params: { Type: "CONSUMER", Active: true } });
		if (res?.status === 200) {
			const consumerList = res.data.filter((c: any) => !c.is_agent);
			consumerList.sort((a: any, b: any) => a.acc_name.localeCompare(b.acc_name));
			setClients(consumerList);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchAllClients);
		}
		setIsClientLoading(false);
	};

	const fetchClientsForAgent = async (agentName: string) => {
		setIsClientLoading(true);
		const res = await api.get(`/invoices/situations/agents/${agentName}/`);
		if (res?.status === 200) {
			const names = [
				...new Set(
					(res.data || [])
						.map(normalizeBill)
						.filter(isPayableBill)
						.map((bill: UnpaidBill) => bill.client)
						.filter(Boolean),
				),
			].sort((a, b) => String(a).localeCompare(String(b)));
			setClients(names.map((name) => ({ acc_name: name })));
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(() => fetchClientsForAgent(agentName));
		} else {
			setClients([]);
		}
		setIsClientLoading(false);
	};

	const fetchBills = async (scope: { agentName?: string; clientName?: string }) => {
		const { agentName, clientName } = scope;
		if (!agentName && !clientName) {
			setAvailableBills([]);
			setIsBillsLoading(false);
			return;
		}

		setIsBillsLoading(true);
		const endpoint = agentName
			? `/invoices/situations/agents/${agentName}/`
			: `/invoices/situations/clients/${clientName}/`;
		const res = await api.get(endpoint);
		if (res?.status === 200) {
			let rows = (res.data || []).map(normalizeBill).filter(isPayableBill);
			if (agentName && clientName) {
				rows = rows.filter((bill: UnpaidBill) =>
					Util.compareStrings(bill.client || "", clientName),
				);
			}
			rows.sort((a: UnpaidBill, b: UnpaidBill) => billSortKey(b) - billSortKey(a));
			setAvailableBills(rows);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(() => fetchBills(scope));
		} else {
			setAvailableBills([]);
		}
		setIsBillsLoading(false);
	};

	const refreshBills = () => {
		fetchBills({
			agentName: selectedAgent?.acc_name,
			clientName: selectedClient?.acc_name,
		});
	};

	const fetchModes = async () => {
		setIsModesLoading(true);
		const res = await api.get("/payment/modes/active");
		if (res?.status === 200) {
			res.data.sort((a: any, b: any) => a.mode_name.localeCompare(b.mode_name));
			setPayModes(res.data);
			if (res.data.length > 0) {
				setSelectedModeId((current) => current || String(res.data[0].id));
			}
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchModes);
		}
		setIsModesLoading(false);
	};

	useEffect(() => {
		if (!isOpen) return;

		setSelectedBills([]);
		setAutoAddOrder("oldest");
		setTotalToPay(0);
		setPayDetails("");
		setSelectedDate(new Date());
		setAmountError("");
		setToViewInvoice(null);

		if (lockAgent) {
			setSelectedAgent({ acc_name: lockedAgentName });
			if (!lockClient) {
				fetchClientsForAgent(lockedAgentName);
			}
		} else {
			setSelectedAgent(null);
		}

		if (lockClient) {
			setSelectedClient({ acc_name: lockedClientName });
		} else {
			setSelectedClient(null);
		}

		if (showAgentPicker && !lockAgent) {
			fetchAgents();
		}
		if (!lockClient && !lockAgent) {
			fetchAllClients();
		}
		fetchModes();
	}, [isOpen, lockedAgentName, lockedClientName, lockAgent, lockClient, showAgentPicker]);

	useEffect(() => {
		if (!isOpen || lockClient) return;
		if (selectedAgent?.acc_name) {
			fetchClientsForAgent(selectedAgent.acc_name);
			if (!lockClient) {
				setSelectedClient(null);
			}
		} else if (!lockAgent) {
			fetchAllClients();
		}
	}, [selectedAgent?.acc_name, isOpen, lockClient, lockAgent]);

	useEffect(() => {
		if (!hasBillScope) {
			setAvailableBills([]);
			setSelectedBills([]);
			return;
		}
		fetchBills({
			agentName: selectedAgent?.acc_name,
			clientName: selectedClient?.acc_name,
		});
		setSelectedBills([]);
	}, [selectedAgent?.acc_name, selectedClient?.acc_name]);

	const availableLeft = useMemo(() => {
		const taken = new Set(selectedBills.map((b) => b.batch));
		return availableBills.filter((b) => !taken.has(b.batch));
	}, [availableBills, selectedBills]);

	const isBillSelected = (batch: string) => selectedBills.some((b) => b.batch === batch);

	const handleAddBill = (bill: UnpaidBill) => {
		if (isBillSelected(bill.batch)) return;

		const currentAllocated = selectedBills.reduce((sum, b) => addMoney(sum, b.payAmount), 0);
		const budgetLeft = Math.max(0, roundMoney(totalToPay - currentAllocated));
		const payAmount = totalToPay > 0 ? Math.min(bill.balance, budgetLeft) : bill.balance;
		if (payAmount <= 0 && totalToPay > 0) return;

		setSelectedBills((prev) => [
			...prev,
			{ ...bill, payAmount: payAmount > 0 ? payAmount : bill.balance },
		]);
	};

	const handleRemoveBill = (bill: SelectedBill) => {
		setSelectedBills((prev) => prev.filter((b) => b.batch !== bill.batch));
	};

	useEffect(() => {
		if (visibleModes.length === 0) return;
		if (visibleModes.some((mode) => String(mode.id) === String(selectedModeId))) return;
		setSelectedModeId(String(visibleModes[0].id));
	}, [visibleModes, selectedModeId]);

	const handleAutoAdd = () => {
		if (totalToPay <= 0) {
			setAmountError("total");
			return;
		}
		setAmountError("");
		setSelectedBills(allocateBills(availableBills, totalToPay, autoAddOrder));
	};

	const handleRemoveAll = () => {
		setSelectedBills([]);
	};

	const updateBillAmount = (batch: string, raw: number) => {
		raw = roundMoney(raw);
		if (!Number.isFinite(raw) || raw < 0) return;
		const bill = selectedBills.find((b) => b.batch === batch);
		if (!bill) return;

		if (compareMoney(raw, bill.balance) > 0) {
			errorRef.current = t("common:payments.bulk.exceedsBillBalance");
			setAmountError(batch);
			setTimeout(() => setAmountError(""), 2500);
			return;
		}

		const otherSum = selectedBills
			.filter((b) => b.batch !== batch)
			.reduce((sum, b) => addMoney(sum, b.payAmount), 0);
		if (totalToPay > 0 && compareMoney(addMoney(otherSum, raw), totalToPay) > 0) {
			errorRef.current = t("common:payments.bulk.exceedsTotalToPay");
			setAmountError(batch);
			setTimeout(() => setAmountError(""), 2500);
			return;
		}

		setSelectedBills((prev) =>
			prev.map((b) => (b.batch === batch ? { ...b, payAmount: raw } : b)),
		);
	};

	const handleSubmit = async () => {
		if (!Util.checkDating(Util.formatISODate(selectedDate), access, 'payment')) return;

		const payload = selectedBills
			.filter((b) => b.payAmount > 0)
			.map((bill) => ({
				invoice: bill.batch,
				amount: bill.payAmount,
				pay_date: Util.formatISODate(selectedDate),
				is_instant:
					Util.formatISODate(selectedDate) === String(bill.date).slice(0, 10),
				mode: parseInt(selectedModeId, 10),
				description: payDetails,
			}));

		setLoadingMessage("SAVE");
		const res = await api.post("/payments", payload);
		if (res?.status === 201) {
			const savedIds: number[] = Array.isArray(res.data?.ids) ? res.data.ids : [];
			toggle();
			onSuccess?.();
			if (savedIds.length > 0) {
				offerReceipt({ source: { kind: "payments", ids: savedIds }, message: t("common:receipt.afterPayment") });
			}
			toast.success(t("common:toast.success.payment"), { autoClose: 3000 });
		} else if (res?.status === 409) {
			toast.error(t("common:toast.error.deposited"), { autoClose: 3000 });
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(handleSubmit);
		}
		setLoadingMessage(null);
	};

	const openInvoice = (bill: UnpaidBill) => {
		setToViewInvoice({ batch: bill.batch });
	};

	const invoiceReferenceButtonSx = {
		background: "none",
		border: "none",
		padding: 0,
		color: theme.palette.primary.main,
		cursor: "pointer",
		textDecoration: "underline",
		fontWeight: 600,
		fontSize: "inherit",
		fontFamily: "inherit",
		textAlign: "left" as const,
	};

	const panelSx = {
		p: 1.5,
		borderRadius: 2,
		border: "1px solid",
		borderColor: "divider",
		height: "100%",
		minHeight: 280,
		display: "flex",
		flexDirection: "column" as const,
	};

	return (
		<>
			<Dialog
				open={isOpen}
				onClose={toggle}
				maxWidth="lg"
				fullWidth
				disableEscapeKeyDown
				sx={{ zIndex: Z_INDEX.dialog }}
				PaperProps={{
					sx: { borderRadius: 3, borderTop: `3px solid ${themeColor}`, overflow: "hidden" },
				}}
			>
				<DialogTitle
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						background: `linear-gradient(180deg, ${alpha(themeColor, 0.07)} 0%, transparent 100%)`,
					}}
				>
					<Stack direction="row" spacing={1.5} alignItems="center">
						<Box
							sx={{
								width: 42,
								height: 42,
								borderRadius: 2,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								bgcolor: alpha(themeColor, 0.12),
								color: themeColor,
							}}
						>
							<PaymentsIcon />
						</Box>
						<Box>
							<Typography variant="h6" fontWeight={700} color={themeColor}>
								{t("common:payments.bulk.title")}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{t("common:payments.bulk.subtitle")}
							</Typography>
						</Box>
					</Stack>
					<IconButton onClick={toggle} aria-label={t("common:ui.buttons.cancel")}>
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent>
					<Paper elevation={0} sx={{ ...panelSx, mb: 2, minHeight: "auto", bgcolor: alpha(themeColor, 0.03) }}>
						<Grid container spacing={2}>
							{showAgentPicker && (
								<Grid size={{ xs: 12, md: lockClient ? 4 : 3 }}>
									<Autocomplete
										size="small"
										options={agents}
										filterOptions={createFilterOptions((a) => a.acc_name || "")}
										getOptionLabel={(a) => a.acc_name || ""}
										value={selectedAgent}
										onChange={(_, v) => setSelectedAgent(v)}
										loading={isAgentLoading}
										disabled={lockAgent || isAgentLoading}
										renderInput={(params) => (
											<TextField
												{...params}
												label={t("common:ui.labels.agent")}
												variant="filled"
												InputProps={{
													...params.InputProps,
													endAdornment: (
														<>
															{!lockAgent && (
																<RefreshButton
																	isWorking={isAgentLoading}
																	onClick={fetchAgents}
																	disabled={isAgentLoading}
																/>
															)}
															{params.InputProps.endAdornment}
														</>
													),
												}}
											/>
										)}
									/>
								</Grid>
							)}
							<Grid size={{ xs: 12, md: showAgentPicker && !lockClient ? 3 : 4 }}>
								<Autocomplete
									size="small"
									options={clients}
									filterOptions={createFilterOptions((c) => c.acc_name || "")}
									getOptionLabel={(c) => c.acc_name || ""}
									value={selectedClient}
									onChange={(_, v) => setSelectedClient(v)}
									loading={isClientLoading}
									disabled={lockClient || isClientLoading || (agentScoped && clients.length === 0)}
									disableClearable={!agentScoped}
									renderInput={(params) => (
										<TextField
											{...params}
											label={
												agentScoped
													? t("common:payments.bulk.clientOptional")
													: t("common:ui.labels.client")
											}
											placeholder={agentScoped ? t("common:payments.bulk.allClients") : undefined}
											variant="filled"
											helperText={agentScoped && !selectedClient ? t("common:payments.bulk.allClientsHint") : " "}
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{!lockClient && (
															<RefreshButton
																isWorking={isClientLoading}
																onClick={() =>
																	selectedAgent?.acc_name
																		? fetchClientsForAgent(selectedAgent.acc_name)
																		: fetchAllClients()
																}
																disabled={isClientLoading}
															/>
														)}
														{params.InputProps.endAdornment}
													</>
												),
											}}
										/>
									)}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 2 }}>
								<TextField
									size="small"
									fullWidth
									type="number"
									label={t("common:payments.bulk.totalToPay")}
									variant="filled"
									value={totalToPay || ""}
									onChange={(e) => {
										const next = Math.max(0, Number(e.target.value) || 0);
										if (walletModeSelected && next > walletCap) {
											setAmountError("total");
											errorRef.current = t("common:wallet.tenderExceeds", { max: Util.formatNumber(walletCap) });
											setTimeout(() => setAmountError(""), 2500);
											return;
										}
										setTotalToPay(next);
										setAmountError("");
									}}
									inputProps={{ min: 0, step: 0.01 }}
									error={amountError === "total"}
									helperText={
										amountError === "total"
											? (errorRef.current || t("common:payments.bulk.enterTotalFirst"))
											: walletModeSelected && clientWallet
												? t("common:wallet.tenderHint", { amount: Util.formatNumber(clientWallet.balance) })
												: " "
									}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<FormControl fullWidth size="small" variant="filled">
									<InputLabel>{t("common:ui.labels.paymentMode")}</InputLabel>
									<Select
										variant="filled"
										value={selectedModeId}
										label={t("common:ui.labels.paymentMode")}
										onChange={(e) => setSelectedModeId(String(e.target.value))}
										disabled={isModesLoading}
									>
										{visibleModes.map((mode) => (
											<MenuItem key={mode.id} value={String(mode.id)}>
												{mode.mode_name}
												{isWalletPayMode(mode.id) && clientWallet
													? ` · ${t("common:wallet.tenderHint", { amount: Util.formatNumber(clientWallet.balance) })}`
													: ""}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, md: 3 }}>
								<LocalizationProvider dateAdapter={AdapterDateFns}>
									<DatePicker
										label={t("common:ui.labels.date")}
										value={selectedDate}
										onChange={(d) => d && setSelectedDate(d instanceof Date ? d : d.toDate())}
										slotProps={{ textField: { size: "small", fullWidth: true, variant: "filled" } }}
									/>
								</LocalizationProvider>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<TextField
									size="small"
									fullWidth
									label={t("common:ui.labels.notes")}
									variant="filled"
									value={payDetails}
									onChange={(e) => setPayDetails(e.target.value)}
								/>
							</Grid>
						</Grid>

						<Box sx={{ mt: 2 }}>
							<Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
								<Typography variant="caption" color="text.secondary">
									{t("common:payments.bulk.allocationProgress")}
								</Typography>
								<Typography variant="caption" fontWeight={700}>
									{formatMoney(allocated)} / {formatMoney(totalToPay)}
									{unallocated > 0 && ` · ${t("common:payments.bulk.unallocated")}: ${formatMoney(unallocated)}`}
								</Typography>
							</Stack>
							<LinearProgress
								variant="determinate"
								value={allocationProgress}
								sx={{
									height: 8,
									borderRadius: 999,
									"& .MuiLinearProgress-bar": {
										bgcolor: overAllocated ? "error.main" : themeColor,
									},
								}}
							/>
						</Box>
					</Paper>

					<Grid container spacing={3}>
						<Grid size={{ xs: 12, md: 6 }}>
							<Paper
								elevation={0}
								sx={{
									display: "flex",
									alignItems: "center",
									flexWrap: "wrap",
									gap: 0.5,
									px: 1.5,
									py: 0.75,
									mb: 1,
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 2,
									bgcolor: alpha(themeColor, 0.03),
								}}
							>
								<RadioGroup
									row
									value={autoAddOrder}
									onChange={(e) => setAutoAddOrder(e.target.value as BulkPaymentOrder)}
									sx={{ flex: 1, minWidth: 0 }}
								>
									<FormControlLabel
										value="oldest"
										control={<Radio size="small" />}
										label={t("common:payments.bulk.orderOldest")}
									/>
									<FormControlLabel
										value="newest"
										control={<Radio size="small" />}
										label={t("common:payments.bulk.orderNewest")}
									/>
								</RadioGroup>
								<Tooltip
									title={t("common:payments.bulk.autoAddTooltip")}
									arrow
									placement="top"
								>
									<span>
										<IconButton
											size="small"
											color="primary"
											onClick={handleAutoAdd}
											disabled={!hasBillScope || availableBills.length === 0}
											aria-label={t("common:payments.bulk.autoAdd")}
										>
											<AutoMode fontSize="small" />
										</IconButton>
									</span>
								</Tooltip>
							</Paper>
						</Grid>
						<Grid size={{ xs: 12, md: 6 }}>
							<Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
								<Tooltip
									title={t("common:payments.bulk.removeAllTooltip")}
									arrow
									placement="top"
								>
									<span>
										<IconButton
											size="small"
											color="error"
											onClick={handleRemoveAll}
											disabled={selectedBills.length === 0}
											aria-label={t("common:payments.bulk.removeAll")}
										>
											<DeleteSweep fontSize="small" />
										</IconButton>
									</span>
								</Tooltip>
							</Stack>
						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
									{t("common:payments.bulk.unpaidBills")} ({availableLeft.length})
								</Typography>
									<RefreshButton
										isWorking={isBillsLoading}
										onClick={refreshBills}
										disabled={!hasBillScope || isBillsLoading}
									/>
							</Stack>
							<TableContainer
								component={Paper}
								elevation={0}
								sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, maxHeight: 400 }}
							>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow>
											<TableCell sx={{ fontWeight: 700 }}>{t("common:tables.invoiceNo")}</TableCell>
											{showClientColumn && (
												<TableCell sx={{ fontWeight: 700 }}>{t("common:tables.client")}</TableCell>
											)}
											<TableCell sx={{ fontWeight: 700 }}>{t("common:tables.date")}</TableCell>
											<TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:tables.owing")}</TableCell>
											<TableCell align="center" sx={{ fontWeight: 700 }} />
										</TableRow>
									</TableHead>
									<TableBody>
										{availableLeft.length === 0 ? (
											<TableRow>
												<TableCell colSpan={showClientColumn ? 5 : 4} align="center">
													<Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 4 }}>
														{isBillsLoading
															? t("common:report.fetchingDetails")
															: t("common:payments.bulk.noUnpaidBills")}
													</Typography>
												</TableCell>
											</TableRow>
										) : (
											availableLeft.map((bill) => {
												const added = isBillSelected(bill.batch);
												const budgetFull = totalToPay > 0 && compareMoney(allocated, totalToPay) >= 0;
												return (
													<TableRow key={bill.batch} sx={{ opacity: added ? 0.4 : 1 }}>
														<TableCell>
															<button
																type="button"
																style={invoiceReferenceButtonSx}
																onClick={() => openInvoice(bill)}
															>
																{bill.reference}
															</button>
														</TableCell>
														{showClientColumn && (
															<TableCell>{bill.client || "—"}</TableCell>
														)}
														<TableCell>{Util.formatDate(bill.date, "medium")}</TableCell>
														<TableCell align="right" sx={{ fontWeight: 700 }}>
															{formatMoney(bill.balance)}
														</TableCell>
														<TableCell align="center">
															<Tooltip title={t("common:payments.bulk.add")}>
																<span>
																	<IconButton
																		size="small"
																		color="primary"
																		disabled={added || budgetFull}
																		onClick={() => handleAddBill(bill)}
																	>
																		<AddCircleOutline fontSize="small" />
																	</IconButton>
																</span>
															</Tooltip>
														</TableCell>
													</TableRow>
												);
											})
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</Grid>

						<Grid size={{ xs: 12, md: 6 }}>
							<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
									{t("common:payments.bulk.selectedBills")} ({selectedBills.length})
								</Typography>
								{selectedBills.length > 0 && (
									<Chip
										label={`${t("common:payments.bulk.allocated")}: ${formatMoney(allocated)}`}
										size="small"
										sx={{ fontWeight: 700, bgcolor: alpha(themeColor, 0.12), color: themeColor }}
									/>
								)}
							</Stack>
							<TableContainer
								component={Paper}
								elevation={0}
								sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, maxHeight: 400 }}
							>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow>
											<TableCell sx={{ fontWeight: 700 }}>{t("common:tables.invoiceNo")}</TableCell>
											{showClientColumn && (
												<TableCell sx={{ fontWeight: 700 }}>{t("common:tables.client")}</TableCell>
											)}
											<TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:tables.owing")}</TableCell>
											<TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:payments.bulk.payAmount")}</TableCell>
											<TableCell align="center" sx={{ fontWeight: 700 }} />
										</TableRow>
									</TableHead>
									<TableBody>
										{selectedBills.length === 0 ? (
											<TableRow>
												<TableCell colSpan={showClientColumn ? 5 : 4} align="center">
													<Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 4 }}>
														{t("common:payments.bulk.noneSelected")}
													</Typography>
												</TableCell>
											</TableRow>
										) : (
											selectedBills.map((bill) => (
												<TableRow key={bill.batch}>
													<TableCell>
														<button
															type="button"
															style={invoiceReferenceButtonSx}
															onClick={() => openInvoice(bill)}
														>
															{bill.reference}
														</button>
														<Typography variant="caption" color="text.secondary" display="block">
															{Util.formatDate(bill.date, "medium")}
														</Typography>
													</TableCell>
													{showClientColumn && (
														<TableCell>{bill.client || "—"}</TableCell>
													)}
													<TableCell align="right">{formatMoney(bill.balance)}</TableCell>
													<TableCell align="right">
														<TextField
															type="number"
															size="small"
															variant="filled"
															value={bill.payAmount}
															onChange={(e) => updateBillAmount(bill.batch, Number(e.target.value))}
															error={amountError === bill.batch}
															inputProps={{ min: 0, step: 0.01, style: { textAlign: "right", width: 72 } }}
															sx={{ width: 96 }}
														/>
													</TableCell>
													<TableCell align="center">
														<Tooltip title={t("common:payments.bulk.remove")}>
															<IconButton
																size="small"
																color="error"
																onClick={() => handleRemoveBill(bill)}
															>
																<RemoveCircleOutline fontSize="small" />
															</IconButton>
														</Tooltip>
													</TableCell>
												</TableRow>
											))
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</Grid>
					</Grid>
				</DialogContent>

				<DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}>
					<Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, flexWrap: "wrap" }}>
						<Chip
							size="small"
							label={`${t("common:payments.bulk.allocated")}: ${formatMoney(allocated)}`}
							color={overAllocated ? "error" : "default"}
						/>
						{unallocated > 0 && (
							<Chip
								size="small"
								variant="outlined"
								label={`${t("common:payments.bulk.unallocated")}: ${formatMoney(unallocated)}`}
							/>
						)}
					</Stack>
					<Button variant="outlined" color="inherit" onClick={toggle}>
						{t("common:ui.buttons.cancel")}
					</Button>
					<Button variant="contained" onClick={() => setConfirmOpen(true)} disabled={!canValidate}>
						{t("common:ui.buttons.confirmPayment")}
					</Button>
				</DialogActions>
			</Dialog>

			<ConfirmDialog
				isOpen={confirmOpen}
				setIsOpen={setConfirmOpen}
				title={t("common:dialog.paymentConfirmation")}
				message={t("common:payments.bulk.confirmMessage", {
					count: selectedBills.filter((b) => b.payAmount > 0).length,
					amount: formatMoney(allocated),
				})}
				confirmBtnColor="success"
				confirmBtnVariant="contained"
				onConfirm={handleSubmit}
			/>

			{toViewInvoice && (
				<Invoice
					isOpen={!!toViewInvoice}
					toggle={() => setToViewInvoice(null)}
					bundle={toViewInvoice}
				/>
			)}
		</>
	);
};

export default BulkPaymentModal;